import type {ILocationType} from '../types/ILocationType';
import type {TranslationFunction} from 'src/types/language-support.ts';
import type {IGetAvailabilityRangeResponse} from '../types/dto/IGetAvailabilityRangeResponse';

import {format} from './date';
import {TZDate} from '@date-fns/tz';
import {addDays, subDays, parse, isBefore, isEqual, isAfter} from 'date-fns';
import {captureSentryMessage} from 'src/utils/sentry';
import {isValidTimezone} from 'src/components/scheduler/lib/timezone-picker/utils';


export const getMeetLinkProviders = (
  t: TranslationFunction,
): Record<string, string> => ({
  google_meet: t('Google_Meet', 'Google Meet'),
  zoom: t('Zoom', 'Zoom'),
  microsoft_teams: t('Microsoft_Teams', 'Microsoft Teams'),
  webex: t('Webex', 'Webex'),
});

export const getLocationTitleLabelMap = (
  t: TranslationFunction,
): Record<
  ILocationType,
  {
    label?: string;
    icon: string;
  }
> => ({
  custom_location: {
    label: t('Custom_Location', 'Custom Location'),
    icon: 'map-pin',
  },
  phone_number: {
    label: t('Phone_Meeting', 'Phone Meeting'),
    icon: 'phone',
  },
  meeting_link: {
    icon: 'video',
  },
});

export const canNavigatePreviousWeek = (
  timezone: string,
  selectedStartDateOfWeek: Date,
  availabilityRange: IGetAvailabilityRangeResponse,
): boolean => {
  const rangeStartDate = new TZDate(
    availabilityRange.start_at * 1000,
    timezone,
  );
  const prevWeekStart = subDays(selectedStartDateOfWeek, 7);
  return (
    isAfter(prevWeekStart, rangeStartDate) ||
    isEqual(prevWeekStart, rangeStartDate)
  );
};

export const canNavigateNextWeek = (
  timezone: string,
  selectedStartDateOfWeek: Date,
  availabilityRange: IGetAvailabilityRangeResponse,
  isTimeSlotsAvailableTillCurrentWeek: boolean,
): boolean => {
  // if time slots are not available till current week and extended end date is available, use extended_end_at as range end date
  let rangeEndDate = new TZDate(availabilityRange.end_at * 1000, timezone);
  if (
    !isTimeSlotsAvailableTillCurrentWeek &&
    availabilityRange.extended_end_at
  ) {
    rangeEndDate = new TZDate(
      availabilityRange.extended_end_at * 1000,
      timezone,
    );
  }
  const nextWeekStart = addDays(selectedStartDateOfWeek, 7);
  return (
    isBefore(nextWeekStart, rangeEndDate) ||
    isEqual(nextWeekStart, rangeEndDate)
  );
};

export const formatAvailabilityTime = (
  time: string,
  language: string,
): string => {
  const date = parse(time, 'HH:mm', new Date());
  return format(date, 'p', {language});
};

export const isTimezoneSupported = (timezone: string) => {
  if (!isValidTimezone(timezone)) {
    captureSentryMessage(`Invalid timezone: ${timezone}`, {
      level: 'error',
      tags: {product: 'scheduler', timezone},
    });
    return false;
  }
  return true;
};

export function getWeekDates(startOfWeek: Date) {
  return Array.from({length: 7}, (_, i) => addDays(startOfWeek, i));
}
